import { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Check, ChevronDown, ChevronRight, ExternalLink, Sparkles } from "lucide-react";
import type {
  BuyerResearchCandidateDto,
  BuyerResearchCandidateRowDto,
  BuyerResearchFlagValue,
} from "@/api/buyerResearch";
import { extractErrorMessage } from "@/api/client";
import { ConfirmModal } from "@/components/ui/PromptModals";
import {
  Button,
  ExceptionBadge,
  Skeleton,
  StatusBadge,
  Textarea,
  Tooltip,
  useToast,
  type SubTableColumn,
} from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { cn } from "@/design-system/lib/utils";
import { listingNameOrFallback } from "@/lib/listingDisplay";
import { useDateFormatter } from "@/utils/datetime";
import { InlineQueryError } from "../queryStates";
import { researchCopy } from "./researchCopy";
import {
  BUYER_FIT_KEYS,
  PROGRAM_FIT_KEYS,
  fitVerdict,
  isNotableCurrency,
  isNotableListingStatus,
  locationLine,
  parseCandidateTags,
  scoreTone,
} from "./researchFormat";
import {
  useAttachCandidate,
  useDismissCandidate,
  useFetchBrokerContact,
  useResearchCandidate,
} from "./useBuyerResearch";

/**
 * One scored listing candidate, as a COMPACT TABLE ROW plus the scoring detail
 * behind its own disclosure.
 *
 * <p>🔴 <b>THE FILE KEEPS ITS NAME AND THE COMPONENT DOES NOT.</b> What used to
 * be `ResearchCandidateCard` — a stack of bordered articles, one per candidate —
 * is now {@link researchCandidateColumns} (the row a specialist scans) and
 * {@link ResearchCandidateDetail} (the drill-in that explains one row's score).
 * The FILE is still `ResearchCandidateCard.tsx` because `clefCardParity.test.ts`
 * addresses it by path and reads {@link LOW_INDUSTRY_CONFIDENCE} out of it as a
 * plain literal; renaming the file is a separate change in a lane that owns that
 * test too.</p>
 *
 * <p>WHAT LIVES WHERE, and the rule that decides it: the ROW carries what a
 * specialist SCANS DOWN A COLUMN — the score, the name, the three money figures
 * — plus the marks that are facts about that row's own subject (Table and Tile
 * Standard §6.3, exceptions inline after the name) and a sub-line with where it
 * is and where it came from. Everything that answers "why did THIS one score
 * that way" is in the detail, because it is read one row at a time and never
 * compared across rows.</p>
 *
 * <p>🔴 <b>TWO SHAPES, AND THE SPLIT IS THE WIRE'S (R5).</b> A row is the narrow
 * {@link BuyerResearchCandidateRowDto} the list endpoint pages; the detail is the
 * full {@link BuyerResearchCandidateDto}, fetched by {@link ResearchCandidateDetailLoader}
 * only when a row is opened. Nothing on the row may read a member the narrow
 * shape does not carry — the type makes that a compile error rather than an
 * `undefined` on screen.</p>
 */

/**
 * 🔴 Mirrors `ClefListingSource.LowIndustryConfidence`. Below it Clef's NAICS
 * classification is DERIVED rather than published by the marketplace, and the
 * detail says so — the server declares the threshold and reads it nowhere,
 * because this label is the only thing it was ever for.
 * `clefCardParity.test.ts` pins the two together, and it matches this line as a
 * PLAIN LITERAL: do not fold it into an options object or compute it.
 */
const LOW_INDUSTRY_CONFIDENCE = 0.6;

/**
 * The industry as WORDS, with the NAICS parenthetical the mapper appended taken
 * back off.
 *
 * <p>🔴 <b>Only the EXACT suffix the server composes is stripped.</b>
 * `ClefListingSource.ComposeIndustry` builds `"{name} ({code})"`, and it returns
 * the BARE CODE when the vendor published no name at all — so a regex hunting
 * for a trailing parenthesis would leave that row showing `(238220)` on one
 * branch and a number with no context on the other. Matching the composed suffix
 * against the code the row carries separately is the only test that cannot
 * mistake a genuine parenthetical in a business's own industry label for
 * provenance.</p>
 *
 * <p>A row written before `naicsCode` existed carries the composed label and no
 * code, so nothing is stripped and the parenthetical stays. That is honest: with
 * no code beside it there is no way to know which part of the string is one.</p>
 */
export function industryInWords(
  industry: string | null | undefined,
  naicsCode: string | null | undefined,
): string | null {
  const label = industry?.trim();
  if (!label) return null;
  const code = naicsCode?.trim();
  if (!code) return label;
  // The name-was-null branch: the label IS the code, so there are no words.
  if (label === code) return null;
  const suffix = ` (${code})`;
  return label.endsWith(suffix) ? label.slice(0, -suffix.length) : label;
}

/** What the row needs from the list around it. */
export interface ResearchCandidateRowContext {
  buyerLeadId: string;
  runId: string;
  canEdit: boolean;
  /** The one candidate whose scoring is open, or null. */
  expandedId: string | null;
  onToggleExpanded: (candidateId: string) => void;
  /**
   * The run's highest batch and how many batches it has, off the list read.
   * With two or more, a row in the latest batch carries "New" and every row
   * names its batch in the sub-line; a run nobody has pulled carries neither,
   * because "Batch 1" on every row of a single search is structure that run
   * does not have.
   */
  latestBatch: number;
  batchCount: number;
  /**
   * A typed Quick search: rows carry their match verdict, "Recommended" is
   * drawn ONLY on a Match, and the detail shows the per-criterion verdicts in
   * place of the buyer-fit group (A7).
   */
  quick?: boolean;
}

/**
 * Column widths under `table-fixed` (recon §11). Tokens where the scale has one;
 * arbitrary rem where it does not, because a table column is a layout width
 * rather than a spacing step. The listing column carries none and takes the
 * rest.
 *
 * <p>🔴 <b>THE ACTIONS COLUMN IS 17rem, NOT THE 12rem THE RECON PROPOSED, AND
 * THE PROPOSAL COULD NOT HAVE HELD.</b> Under automatic layout a `w-[12rem]`
 * cell is a MINIMUM and grew to fit Attach, the 32px gap (R17) and Dismiss; under
 * fixed layout the width binds, and the three do not fit in it. Measured in
 * Chromium: Attach 93px and Dismiss 98px with the real Montserrat, 94px and
 * 102px in the sandbox's fallback face — 223–228px of content against the 160px
 * a 12rem cell leaves inside its padding. The row right-aligns its buttons with
 * `justify-end`, so the surplus did not scroll: it spilled LEFT over the revenue
 * figure, which no scroll width can see and which
 * `e2e/buyer-research-vertical-cost.spec.ts` now asserts per cell.</p>
 *
 * <p>17rem leaves a 240px content box: 12px of headroom over the wider face at
 * rest. While a request is in flight its button gains a spinner and its gap
 * (16px + 8px, derived from `Button`, not measured), so the row can overshoot by
 * up to 12px — into the revenue cell's 16px right padding, short of the figure.
 * Every rem added here comes out of the listing column, which is why this is
 * the smallest whole rem that holds the buttons rather than a round number with
 * room to spare.</p>
 *
 * <p>🔴 <b>THE ATTACHED ROW (D1) FITS BY ITS GAP, NOT BY A WIDER COLUMN.</b> An
 * attached row keeps a disabled "Attached ✓" in Attach's place: 136px in the
 * sandbox's fallback face. With the 32px gap the row is 266px against this
 * 240px box and spilled 10px over the revenue cell (the e2e per-cell check
 * caught it). Widening to 19rem fixed that and broke the LISTING cell instead:
 * at 1440px a long title with Recommended, Attached and the implausible badge
 * needs 416px, and 17rem is the widest whole rem that leaves it that. So the
 * attached row uses the 8px gap — the 32px exists to keep Dismiss away from a
 * live constructive control, and a disabled one cannot be mis-clicked — making
 * it 246px: 6px into its own cell's padding, inside the 12px the in-flight
 * spinner above already takes. Dismiss is right-anchored, so it does not move.</p>
 */
const FIT_COLUMN_CLASS = "w-[5rem]";
const MONEY_COLUMN_CLASS = "w-[7rem] whitespace-nowrap";
const ACTIONS_COLUMN_CLASS = "w-[17rem]";

/**
 * The compact row.
 *
 * <p>🔴 <b>NOT ONE COLUMN CARRIES A `sortAccessor`, and that is the mechanism
 * rather than a style.</b> `SubTable` computes `sortable = hasChrome &&
 * !!col.sortAccessor`, so an omitted accessor suppresses the affordance at any
 * row count. The rows are ONE PAGE of what the server ordered (R3/R5); sorting
 * that page in the browser would order twenty-five rows of a list of hundreds
 * and call it the list. The sort control above the table asks the server
 * instead.</p>
 *
 * <p>🔴 <b>THE WIDTHS ASSUME `table-fixed`</b>, which the list sets on the table.
 * Under automatic layout a `truncate` cell forbids wrapping and each column grew
 * to its longest value, which is what pushed Attach and Dismiss out of the
 * panel (§11). Fixed layout takes the widths from the header row: fit, the three
 * money columns and the actions are fixed, and the listing takes the rest and
 * truncates with a Tooltip.</p>
 */
export function researchCandidateColumns(
  ctx: ResearchCandidateRowContext,
): SubTableColumn<BuyerResearchCandidateRowDto>[] {
  const copy = researchCopy.results;
  return [
    {
      key: "fit",
      header: copy.columns.fit,
      className: FIT_COLUMN_CLASS,
      render: (c) => <FitCell candidate={c} ctx={ctx} />,
    },
    {
      key: "listing",
      header: copy.columns.listing,
      // No width: under `table-fixed` the one column without one takes the rest.
      className: "min-w-0",
      render: (c) => <ListingCell candidate={c} ctx={ctx} />,
    },
    {
      key: "asking",
      header: copy.columns.asking,
      align: "right",
      className: MONEY_COLUMN_CLASS,
      render: (c) => <Money value={c.askingPrice} />,
    },
    {
      key: "cashFlow",
      header: copy.columns.cashFlow,
      align: "right",
      className: MONEY_COLUMN_CLASS,
      render: (c) => <CashFlowCell candidate={c} />,
    },
    {
      key: "revenue",
      header: copy.columns.revenue,
      align: "right",
      className: MONEY_COLUMN_CLASS,
      render: (c) => <Money value={c.revenue} />,
    },
    {
      key: "actions",
      header: copy.columns.actions,
      align: "right",
      className: ACTIONS_COLUMN_CLASS,
      render: (c) =>
        ctx.canEdit && !c.isDismissed ? (
          <RowActions
            candidate={c}
            buyerLeadId={ctx.buyerLeadId}
            runId={ctx.runId}
            attached={c.isAttached}
          />
        ) : null,
    },
  ];
}

/**
 * The score, and the disclosure that explains it.
 *
 * <p>🔴 <b>The score is a SIBLING of the button, not its contents.</b> An
 * `aria-label` on a button replaces everything inside it, so folding the badge
 * in would have announced the action and swallowed the number — in the one
 * column whose whole job is to carry it. Separate, the cell announces the score
 * and the control announces what it opens.</p>
 *
 * <p>🔴 <b>`onRowClick` is deliberately unused.</b> `SubTable` offers it and
 * gives the keyboard half for free, but this row already carries an outbound
 * link and two buttons: a row-level click either double-fires over them or
 * swallows them, and neither failure is visible in the markup. The same call the
 * send-preview attachment picker makes, for the same reason.</p>
 */
function FitCell({
  candidate: c,
  ctx,
}: {
  candidate: BuyerResearchCandidateRowDto;
  ctx: ResearchCandidateRowContext;
}) {
  const copy = researchCopy.results;
  const open = ctx.expandedId === c.id;
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <span className="inline-flex items-center gap-space-1">
      {/* R6 — an unscored row is a blank, not a pill: a muted em-dash on
          screen, and the words for anyone who cannot see it. */}
      {c.fitScore == null ? (
        <span className="text-gray-mid" data-testid="research-score" data-unscored="true">
          <span aria-hidden>—</span>
          <span className="sr-only">{researchCopy.card.notScored}</span>
        </span>
      ) : (
        <StatusBadge tone={scoreTone(c.fitScore)} data-testid="research-score">
          {`${c.fitScore}`}
        </StatusBadge>
      )}
      <button
        type="button"
        onClick={() => ctx.onToggleExpanded(c.id)}
        aria-expanded={open}
        aria-controls={open ? detailId(c.id) : undefined}
        aria-label={open ? copy.collapseRow(c.title) : copy.expandRow(c.title)}
        className={cn(
          "inline-flex items-center rounded-md p-[3px] text-gray-mid",
          "transition-colors duration-fast ease-standard hover:text-onyx",
          "focus-visible:outline-none focus-visible:shadow-goldRing",
        )}
        data-testid="research-expand"
      >
        <Chevron size={14} strokeWidth={2} aria-hidden />
      </button>
    </span>
  );
}

/** The id the detail region carries, so the disclosure can point at it. */
function detailId(candidateId: string): string {
  return `research-detail-${candidateId}`;
}

/**
 * Who this listing is, and the marks that are facts about it.
 *
 * <p>§6.3 — an exception sits inline after the NAME rather than in a column of
 * its own, which would be whitespace on every healthy row. §11.1/§11.4 — the
 * title is one line with an ellipsis, and the whole of it is reachable on hover
 * AND on keyboard focus through the shared `Tooltip`; never a native `title`,
 * which opens on neither.</p>
 *
 * <p>The sub-line carries where the listing is, what it is, where it came from
 * and — on a pulled run — which batch it arrived in. The marketplace moved here
 * when the Source column went (R17): it is provenance a reader glances at, not
 * a figure they scan down.</p>
 */
function ListingCell({
  candidate: c,
  ctx,
}: {
  candidate: BuyerResearchCandidateRowDto;
  ctx: ResearchCandidateRowContext;
}) {
  const copy = researchCopy.card;
  const results = researchCopy.results;
  const isClef = c.clefListingId !== null;
  const hasPublicLink = Boolean(c.sourceUrl);
  // LIVE, per buyer (the server's `isAttached`), never the row's own attach
  // stamp: the stamp outlives a removal on Associated listings, and a row on
  // another run that bought the same business carries none.
  const attached = c.isAttached;
  const pulled = ctx.batchCount >= 2;
  const isNew = pulled && c.batchNumber === ctx.latestBatch;
  const locationText = locationLine(c.city, c.state) ?? (isClef ? copy.geographyUnknown : null);
  // "Industry in words" — the NAICS code is provenance and lives in the detail.
  const industry = industryInWords(c.industry, c.naicsCode);
  // The marketplace Clef found it on, by name. Clef itself is how Phoenix
  // reached it, not where the listing lives, so it is named only when the
  // vendor gave no marketplace at all.
  const source = isClef ? (c.marketplaceName ?? c.sourceDisplayName) : c.sourceDisplayName;
  const batch = pulled ? results.batch(c.batchNumber) : null;
  // ONE expression, read by the Tooltip's label and by the line it labels, so a
  // clipped value and the text revealed on hover cannot drift apart.
  const whereParts = [
    { key: "location", text: locationText, testId: null },
    { key: "industry", text: industry, testId: null },
    { key: "source", text: source, testId: "research-source" },
    { key: "batch", text: batch, testId: "research-batch" },
  ].filter((p): p is { key: string; text: string; testId: string | null } => Boolean(p.text));
  const whereText = whereParts.map((p) => p.text).join(" · ");
  const implausibleText = c.figuresImplausible ? implausibleExplanation(c.implausibleReasons) : null;

  return (
    <div className="min-w-0" data-candidate-id={c.id}>
      <div className="flex min-w-0 items-center gap-space-2">
        {hasPublicLink ? (
          // The DOM text is the whole title — the cut is CSS — so the link's
          // accessible name is complete and needs no `aria-label` beside it.
          <Tooltip label={c.title}>
            <a
              href={c.sourceUrl!}
              target="_blank"
              rel="noopener noreferrer"
              className="truncate text-small font-semibold text-gold-shadow hover:underline"
            >
              {c.title}
            </a>
          </Tooltip>
        ) : (
          <Tooltip label={c.title}>
            <span className="truncate text-small font-semibold text-onyx">{c.title}</span>
          </Tooltip>
        )}
        {hasPublicLink && (
          <ExternalLink size={12} strokeWidth={1.5} aria-hidden className="shrink-0 text-gray-mid" />
        )}
        {/* Only on a pulled run: "New" on every row of a single search is a
            claim about an arrival that never happened. */}
        {isNew && (
          <StatusBadge tone="progress" className="shrink-0" data-testid="research-new">
            {results.newBadge}
          </StatusBadge>
        )}
        {c.recommended && (!ctx.quick || c.matchStatus === "Match") && (
          <StatusBadge tone="ok" className="shrink-0" data-testid="research-recommended">
            <Sparkles size={12} aria-hidden />
            {copy.recommended}
          </StatusBadge>
        )}
        {ctx.quick && <QuickMatchBadge status={c.matchStatus ?? null} reason={c.matchReason ?? null} />}
        {/* D1 — ONE attached marker, on every run kind. */}
        {attached && (
          <StatusBadge tone="ok" className="shrink-0" data-testid="research-attached-badge">
            <Check size={12} aria-hidden />
            {copy.attached}
          </StatusBadge>
        )}
        {c.isDismissed && (
          <StatusBadge tone="muted" struck className="shrink-0" data-testid="research-dismissed">
            {copy.dismissed}
          </StatusBadge>
        )}
        {c.linkOnly && (
          <StatusBadge tone="muted" className="shrink-0" data-testid="research-link-only">
            {copy.linkOnly(c.sourceDisplayName)}
          </StatusBadge>
        )}
        {/* R10 — the figures are flagged, never corrected and never hidden
            from this row: the reader sees the number the seller published and
            is told why it looks wrong. The reason is in the badge's own text
            for a screen reader and in the shared Tooltip for everyone else —
            focusable, so the Tooltip opens on the keyboard too. */}
        {implausibleText && (
          <Tooltip label={implausibleText}>
            <StatusBadge tone="warn" tabIndex={0} className="shrink-0" data-testid="research-implausible">
              <AlertTriangle size={12} aria-hidden />
              {results.implausibleBadge}
              <span className="sr-only">{`: ${implausibleText}`}</span>
            </StatusBadge>
          </Tooltip>
        )}
        {/* §6.4 — amber is "give this a look", and a listing already under
            contract is exactly that. Gated harder than null: the mapper drops a
            delisted listing, so every row it keeps reads `active`, and a badge
            on every row is noise a reader learns to stop seeing. */}
        {isNotableListingStatus(c.status) && (
          <ExceptionBadge severity="attention" className="shrink-0" data-testid="research-listing-status">
            {copy.status(c.status!)}
          </ExceptionBadge>
        )}
      </div>
      {whereText && (
        // §11.1/§11.4 again, and it is the same rule as the title above: a
        // clipped value must be reachable on hover AND on keyboard focus.
        <Tooltip label={whereText}>
          <p className="m-0 truncate text-caption text-gray-mid" data-testid="research-where">
            {whereParts.map((part, i) => (
              <Fragment key={part.key}>
                {i > 0 && " · "}
                {part.testId ? <span data-testid={part.testId}>{part.text}</span> : part.text}
              </Fragment>
            ))}
          </p>
        </Tooltip>
      )}
    </div>
  );
}

/**
 * The implausible-figures reasons as one sentence per code. An unknown code —
 * a reason the server added after this client shipped — reads as the generic
 * sentence rather than as a raw token.
 */
function implausibleExplanation(reasons: readonly string[]): string {
  const map = researchCopy.results.implausibleReasons as Record<string, string | undefined>;
  const sentences = reasons.map((r) => map[r] ?? researchCopy.results.implausibleUnknown);
  const unique = Array.from(new Set(sentences));
  return unique.length > 0 ? unique.join(" ") : researchCopy.results.implausibleUnknown;
}

/**
 * 🔴 §7 — <b>A PLAIN MISSING VALUE IS AN EM-DASH AND NOTHING ELSE.</b> Not
 * "Not stated", which is prose that reads correctly inside a sentence and reads
 * as data in a column; not a pill, in the three columns a specialist scans
 * straight down. A seller who chose not to publish a figure has left a blank,
 * not a task with a next step, so there is no door either.
 */
function Money({ value }: { value: number | null }) {
  if (value == null) {
    return <span className="text-gray-mid">{researchCopy.results.missingMoney}</span>;
  }
  return <>{formatCurrency(value)}</>;
}

/**
 * The cash flow, and WHICH published figure it came from.
 *
 * The basis stays beside the number rather than moving into the detail: on
 * `ebitda` the program-fit multiple is `unknown` BY RULE, so a reader comparing
 * the column needs the basis to know whether they are comparing like with like.
 * A second muted line costs no row height — the identity cell is two lines on
 * every row already.
 */
function CashFlowCell({ candidate: c }: { candidate: BuyerResearchCandidateRowDto }) {
  const isClef = c.clefListingId !== null;
  const basis = isClef && c.cashFlowBasis ? (researchCopy.card.cashFlowBasis[c.cashFlowBasis] ?? null) : null;
  return (
    <>
      <Money value={c.cashFlow} />
      {basis && c.cashFlow !== null && (
        <div className="text-caption font-normal text-gray-mid" data-testid="research-cash-flow-basis">
          {basis}
        </div>
      )}
    </>
  );
}

/**
 * Attach and Dismiss, each behind a confirm.
 *
 * <p>🔴 <b>DISMISS IS PUSHED AWAY FROM EVERYTHING ELSE.</b> It is the one
 * control here that writes a decision lasting beyond this run — a dismissal is
 * PER BUYER and survives every later search — and in a dense row a destructive
 * control adjacent to a constructive one is a mis-click waiting for a tired
 * afternoon. The gap is a token (`space-5`, 32px) rather than a measured pixel
 * value, per the brand rule, and it is applied only when there is something to
 * its left to be separated FROM.</p>
 *
 * <p>The attach confirm NAMES what will happen — reuse an existing listing, or
 * create one — because the two are different writes with different consequences
 * for the catalog, and the server has already decided which; the row only
 * relays it.</p>
 */
function RowActions({
  candidate,
  buyerLeadId,
  runId,
  attached,
}: {
  candidate: BuyerResearchCandidateRowDto;
  buyerLeadId: string;
  runId: string;
  attached: boolean;
}) {
  const copy = researchCopy.card;
  const toast = useToast();
  const [confirmingAttach, setConfirmingAttach] = useState(false);
  const [confirmingDismiss, setConfirmingDismiss] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const attach = useAttachCandidate(buyerLeadId);
  const dismiss = useDismissCandidate(buyerLeadId);

  const existingName = candidate.existingListingId
    ? listingNameOrFallback(candidate.existingListingDisplayName)
    : null;

  return (
    <div className="flex items-center justify-end">
      {/* D1 — an attached row keeps a control in ATTACH's place, disabled, so
          the row does not shift and cannot be attached twice. `loading`
          disables ATTACH while the request is in flight (D2). */}
      {attached ? (
        <Button size="sm" disabled data-testid="research-attached-button">
          {copy.attachedButton}
          <Check size={14} aria-hidden />
        </Button>
      ) : (
        <Button
          size="sm"
          onClick={() => {
            setError(null);
            setConfirmingAttach(true);
          }}
          loading={attach.isPending}
          data-testid="research-attach"
        >
          {copy.attach}
        </Button>
      )}
      <Button
        size="sm"
        variant="secondary"
        className={attached ? "ml-space-2" : "ml-space-5"}
        onClick={() => {
          setError(null);
          setConfirmingDismiss(true);
        }}
        loading={dismiss.isPending}
        data-testid="research-dismiss"
      >
        {copy.dismiss}
      </Button>

      {error && (
        <p className="m-0 ml-space-2 text-caption text-status-error" role="alert">
          {error}
        </p>
      )}

      <ConfirmModal
        open={confirmingAttach}
        title={copy.attachTitle}
        message={existingName ? copy.attachReuse(existingName) : copy.attachCreate}
        confirmLabel={copy.attachConfirm}
        onCancel={() => setConfirmingAttach(false)}
        onConfirm={() => {
          setConfirmingAttach(false);
          attach.mutate(
            { runId, candidateId: candidate.id },
            {
              onSuccess: (r) => toast.success(copy.attachedToast(r.listingDisplayName, r.reused)),
              onError: (e: unknown) => setError(extractErrorMessage(e)),
            },
          );
        }}
      />

      <ConfirmModal
        open={confirmingDismiss}
        title={copy.dismissTitle}
        destructive
        message={
          <div className="grid gap-space-2">
            <p className="m-0">{copy.dismissMessage}</p>
            <label className="block">
              <span className="text-caption uppercase tracking-wide text-gray-mid">
                {copy.dismissReason}
              </span>
              <Textarea
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={copy.dismissReasonPlaceholder}
                aria-label={copy.dismissReason}
                className="mt-space-1"
                data-testid="research-dismiss-reason"
              />
            </label>
          </div>
        }
        confirmLabel={copy.dismissConfirm}
        onCancel={() => setConfirmingDismiss(false)}
        onConfirm={() => {
          setConfirmingDismiss(false);
          dismiss.mutate(
            { runId, candidateId: candidate.id, reason },
            {
              onSuccess: () => {
                setReason("");
                toast.success(copy.dismissedToast);
              },
              onError: (e: unknown) => setError(extractErrorMessage(e)),
            },
          );
        }}
      />
    </div>
  );
}

/**
 * The detail for ONE opened row, fetched on open (R5). The row carries none of
 * the scoring, so the list read stays small however many rows a page holds;
 * the price is one request per row a reader actually opens.
 *
 * <p>The id region is rendered on every branch, so the disclosure's
 * `aria-controls` points at something while the request is still in flight.</p>
 */
export function ResearchCandidateDetailLoader({
  buyerLeadId,
  runId,
  candidateId,
  canEdit,
  quick = false,
}: {
  buyerLeadId: string;
  runId: string;
  candidateId: string;
  canEdit: boolean;
  quick?: boolean;
}) {
  const detail = useResearchCandidate(buyerLeadId, runId, candidateId);
  if (detail.data) {
    return (
      <ResearchCandidateDetail
        candidate={detail.data}
        buyerLeadId={buyerLeadId}
        runId={runId}
        canEdit={canEdit}
        quick={quick}
      />
    );
  }
  return (
    <div id={detailId(candidateId)} data-testid="research-candidate-detail-loading">
      {detail.isError ? (
        <InlineQueryError
          what={researchCopy.results.detailLoadFailedWhat}
          onRetry={() => void detail.refetch()}
          retrying={detail.isFetching}
        />
      ) : (
        <Skeleton className="h-16 w-full" />
      )}
    </div>
  );
}

/**
 * Why this candidate scored the way it did — the drill-in under its row.
 *
 * <p>Everything here is read ONE ROW AT A TIME and never compared across rows,
 * which is the test that put it behind a disclosure rather than in a column: the
 * two fit groups alone are eleven checks, and eleven columns of mostly-passing
 * verdicts is a table nobody can scan for the two that can cost a candidate its
 * recommendation.</p>
 *
 * <p>A link-only source (the CoStar family — D11) was never READ by Phoenix, so
 * this says so where a contact block would otherwise be, and shows no contact
 * block at all: a name Phoenix did not read is a name it cannot vouch for.</p>
 */
export function ResearchCandidateDetail({
  candidate,
  buyerLeadId,
  runId,
  canEdit,
  quick = false,
}: {
  candidate: BuyerResearchCandidateDto;
  buyerLeadId: string;
  runId: string;
  canEdit: boolean;
  /** A7 — a quick run has no buyer brief, so no buyer-fit group. */
  quick?: boolean;
}) {
  const c = candidate;
  const copy = researchCopy.card;
  const { date } = useDateFormatter();
  const attached = c.isAttached;
  const showContact = !c.linkOnly;
  const hasShownContact =
    c.contactVia === "shown" &&
    Boolean(c.brokerName || c.brokerFirm || c.brokerPhone || c.brokerEmail);

  // 🔴 EVERY Clef affordance below gates on the ID, never on
  // `sourceKey === "clef"`. A row from the retired OAuth integration carries the
  // key and no id, and each of these needs something to address — so an id-less
  // row degrades quietly instead of offering a fetch that can only 400, or
  // disclaiming figures it cannot re-read.
  const isClef = c.clefListingId !== null;

  // Clef returns listings whose marketplace published no URL at all; the
  // provider stores an empty string for those rather than inventing one, so the
  // detail says where the listing is instead of the row rendering a dead link.
  const hasPublicLink = c.sourceUrl.length > 0;

  const lowConfidenceIndustry =
    isClef &&
    Boolean(c.industry) &&
    c.industryConfidence !== null &&
    c.industryConfidence < LOW_INDUSTRY_CONFIDENCE;

  const contactFetched = c.contactFetchedAtUtc !== null;

  // ---- Layer B carriers (WS5) --------------------------------------------
  //
  // 🔴 EVERY ONE IS NULL ON EVERY ROW WRITTEN BEFORE THE COLUMNS EXISTED, on
  // every non-Clef source, and `employees` on every row full stop until
  // `ClefApiClient.ReadInt` stops requiring a JSON number for a field the
  // vendor types as a STRING. There is no backfill, so each is gated — a label
  // that renders an empty value on the whole historical population is worse
  // than one that does not render. `status` is gated harder still and lives on
  // the row itself, where an exception belongs.
  const tags = parseCandidateTags(c.tagsJson);
  const notableCurrency = isNotableCurrency(c.currency);

  return (
    <div
      id={detailId(c.id)}
      className="grid gap-space-3"
      data-testid="research-candidate-detail"
      data-candidate-id={c.id}
    >
      {c.reasons.length > 0 && (
        <ul className="m-0 list-disc pl-space-4 text-caption text-gray-mid grid gap-[2px]">
          {c.reasons.map((r, i) => (
            <li key={`${i}-${r}`}>{r}</li>
          ))}
        </ul>
      )}

      {c.summary && <p className="m-0 text-small text-onyx">{c.summary}</p>}
      {c.recommendationNote && <p className="m-0 text-caption text-onyx">{c.recommendationNote}</p>}
      {c.sellerFinancingMentioned && c.sellerFinancingQuote && (
        <p className="m-0 text-caption text-gray-mid">
          <span className="font-semibold">{copy.sellerFinancingQuote}</span> “{c.sellerFinancingQuote}”
        </p>
      )}

      <div className="grid gap-space-3 sm:grid-cols-2">
        {quick ? (
          <QuickCriteria criteria={c.criteria ?? []} />
        ) : (
          <FitGroup
            label={copy.buyerFit}
            keys={BUYER_FIT_KEYS}
            labels={researchCopy.buyerFitLabels}
            values={c.buyerFit}
            testId="research-buyer-fit"
          />
        )}
        <FitGroup
          label={copy.programFit}
          keys={PROGRAM_FIT_KEYS}
          labels={researchCopy.programFitLabels}
          values={c.programFit}
          testId="research-program-fit"
        />
      </div>

      {/* Clef's own tags, out of the stored JSON array. 🔴 An unparseable or
          non-array blob yields NOTHING rather than the raw string: the column
          holds the vendor's payload verbatim, and printing brace-and-quote soup
          at a reader looks like a defect in Phoenix. The vendor's order is kept,
          because it is the only ordering anybody has. */}
      {tags.length > 0 && (
        <ul
          className="m-0 p-0 list-none flex flex-wrap gap-space-1"
          aria-label={copy.tagsHeading}
          data-testid="research-tags"
        >
          {tags.map((t, i) => (
            <li
              key={`${i}-${t}`}
              className="rounded-md border border-border-light px-space-2 py-[2px] text-caption text-gray-mid"
            >
              {t}
            </li>
          ))}
        </ul>
      )}

      <div className="grid gap-[2px] text-caption text-gray-mid">
        {/* D13 — the same sentence written into the listing this candidate
            creates. It sits here rather than in a tooltip for the reason the
            standard gives for every disclaimer: one a reader has to hover for is
            one they will not see before acting. */}
        {isClef && <p className="m-0" data-testid="research-figures-unverified">{copy.figuresUnverified}</p>}
        {/* Only when it is NOT USD: an unannounced non-dollar figure reads as
            dollars, while "Figures in USD" on every row is noise. */}
        {notableCurrency && (
          <p className="m-0" data-testid="research-currency">{copy.currency(c.currency!)}</p>
        )}
        {c.subIndustry && (
          <p className="m-0" data-testid="research-sub-industry">{copy.subIndustry(c.subIndustry)}</p>
        )}
        {lowConfidenceIndustry && (
          <p className="m-0" data-testid="research-low-confidence-industry">
            {copy.lowConfidenceIndustry}
          </p>
        )}
        {/* 🔴 THE CLASSIFICATION AS THE SERVER COMPOSED IT — provenance for the
            words the row states, and never a column: a number where a reader
            needs a noun is what putting the NAICS code on the row would be.

            Rendered VERBATIM rather than through a sentence of its own, and
            only when it differs from what the row already shows. The mapper
            builds `"{name} ({code})"`, so where nothing was stripped this would
            be the row's own caption a second time; where something was, this
            line is the only place the code survives. No new copy: the label is
            the vendor's classification and says what it is. */}
        {c.industry && industryInWords(c.industry, c.naicsCode) !== c.industry && (
          <p className="m-0" data-testid="research-naics">
            {c.industry}
          </p>
        )}
        {c.dealType && <p className="m-0" data-testid="research-deal-type">{copy.dealType(c.dealType)}</p>}
        {(c.employees || c.lastChangeAt) && (
          <p className="m-0 break-words" data-testid="research-layer-b">
            {[
              c.employees ? copy.employees(c.employees) : null,
              c.lastChangeAt ? `${copy.lastChange} ${date(c.lastChangeAt)}` : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        )}
        {isClef && !hasPublicLink && (
          <p className="m-0" data-testid="research-no-public-link">{copy.noPublicLink}</p>
        )}
        {c.alsoSeenAt.length > 0 && <p className="m-0">{copy.alsoSeenAt(c.alsoSeenAt.length)}</p>}
        {c.possibleDuplicateOfListingId && (
          <p className="m-0" data-testid="research-possible-duplicate">
            {copy.possibleDuplicate(listingNameOrFallback(c.possibleDuplicateDisplayName))}
          </p>
        )}
      </div>

      {showContact && (
        <div className="text-caption text-onyx" data-testid="research-contact">
          {hasShownContact ? (
            <dl className="m-0 grid gap-[2px]">
              {c.brokerName && <ContactLine value={c.brokerName} />}
              {c.brokerFirm && <ContactLine value={c.brokerFirm} />}
              {c.brokerPhone && <ContactLine value={c.brokerPhone} />}
              {c.brokerEmail && <ContactLine value={c.brokerEmail} />}
            </dl>
          ) : isClef && contactFetched ? (
            // Asked and answered: Clef held nothing. Offering the button again
            // would sell the same nothing for another record.
            <p className="m-0 text-gray-mid" data-testid="research-contact-none">
              {copy.brokerContactNoneOnFile}
            </p>
          ) : isClef && canEdit && !c.isDismissed ? (
            <BrokerContactAction candidate={c} buyerLeadId={buyerLeadId} runId={runId} />
          ) : (
            <p className="m-0 text-gray-mid">{copy.contactVia(c.sourceDisplayName)}</p>
          )}
        </div>
      )}

      {attached && (
        <p className="m-0 text-caption text-onyx" data-testid="research-attached">
          {copy.attached} ·{" "}
          <Link to="?tab=associated-listings" className="text-gold-shadow hover:underline">
            {copy.attachedOpen}
          </Link>
        </p>
      )}
    </div>
  );
}

/**
 * D7 — buy the listing broker's details for this candidate.
 *
 * <p>🔴 Offered only where the candidate carries a `clefListingId`, because the
 * call addresses that id; and only to a reader who may edit, because it spends a
 * record from an allowance the whole firm shares. The hint says so before the
 * press rather than after it.</p>
 *
 * <p>The mutation invalidates rather than patching the row, so the run's
 * records-used figure moves with the contact it paid for.</p>
 */
function BrokerContactAction({
  candidate,
  buyerLeadId,
  runId,
}: {
  candidate: BuyerResearchCandidateDto;
  buyerLeadId: string;
  runId: string;
}) {
  const copy = researchCopy.card;
  const [error, setError] = useState<string | null>(null);
  const fetchContact = useFetchBrokerContact(buyerLeadId);

  return (
    <div className="grid gap-space-1">
      <p className="m-0 text-gray-mid">{copy.getBrokerContactHint}</p>
      {error && (
        <p className="m-0 text-status-error" role="alert">
          {error}
        </p>
      )}
      <div>
        <Button
          size="sm"
          variant="secondary"
          loading={fetchContact.isPending}
          data-testid="research-get-broker-contact"
          onClick={() => {
            setError(null);
            fetchContact.mutate(
              { runId, candidateId: candidate.id },
              { onError: (e: unknown) => setError(extractErrorMessage(e)) },
            );
          }}
        >
          {copy.getBrokerContact}
        </Button>
      </div>
    </div>
  );
}

function ContactLine({ value }: { value: string }) {
  return <dd className="m-0">{value}</dd>;
}

/**
 * One labelled fit group, in the FIXED key order — never the dictionary's own.
 * The server sends exactly these keys, and a key that is missing renders as
 * "Not stated" rather than vanishing, so both groups have the same shape on
 * every candidate.
 *
 * <p>🔴 <b>ELEVEN CHECKS ARE NOT ELEVEN PILLS.</b> Every verdict used to be a
 * three-colour badge, so a candidate meeting the program cleanly still painted
 * several reds for want of a published figure — the inflation §6.1 names by its
 * consequence: <i>"makes an ordinary book look like a wall of problems, and by
 * the time a real exception appears the reader has stopped seeing pills."</i>
 * The severity rule is {@link fitVerdict}'s and is NOT restated here, because
 * the two keys that can cost a candidate its recommendation are read off the
 * server's own gates and a second copy of that set would be free to drift from
 * them.</p>
 *
 * <p>§6 maps the three tones: a pass needs no mark at all, a fact with no
 * consequence is muted PROSE rather than a grey pill, and only the blocking case
 * is an exception — an outline pill, which is the one thing on this panel a
 * reader's eye should be caught by.</p>
 */
/**
 * A quick-search row's verdict against what was typed. A Match needs no mark;
 * "couldn't confirm" is attention, "didn't match" is a muted state. The
 * server's reason rides the badge for a screen reader and the shared Tooltip
 * for everyone else (focusable, so it opens on the keyboard too).
 */
function QuickMatchBadge({ status, reason }: { status: string | null; reason: string | null }) {
  if (status !== "Unconfirmed" && status !== "NoMatch") return null;
  const word = status === "Unconfirmed" ? researchCopy.quick.criterionValue.unknown : researchCopy.quick.didNotMatch;
  const badge = (
    <StatusBadge
      tone={status === "Unconfirmed" ? "warn" : "muted"}
      tabIndex={reason ? 0 : undefined}
      className="shrink-0"
      data-testid="research-match-reason"
      data-match-status={status}
    >
      {word}
      {reason && <span className="sr-only">{`: ${reason}`}</span>}
    </StatusBadge>
  );
  return reason ? <Tooltip label={reason}>{badge}</Tooltip> : badge;
}

/** A7 — each criterion read from the typed search, with its verdict and why. */
function QuickCriteria({ criteria }: { criteria: { key: string; label: string; value: BuyerResearchFlagValue; reason: string | null }[] }) {
  const copy = researchCopy.quick;
  return (
    <div data-testid="research-quick-criteria">
      <h4 className="m-0 mb-space-1 text-caption uppercase tracking-wide text-gray-mid">{copy.criteriaHeading}</h4>
      <ul className="m-0 p-0 list-none grid gap-space-1">
        {criteria.map((cr) => (
          <li key={cr.key} className="grid gap-[2px] text-caption" data-criterion-key={cr.key}>
            <span className="flex items-center justify-between gap-space-2">
              <span className="text-onyx">{cr.label}</span>
              <span
                className={cr.value === "yes" ? "text-onyx" : cr.value === "no" ? "text-status-error" : "text-gray-mid"}
                data-criterion-value={cr.value}
              >
                {copy.criterionValue[cr.value] ?? cr.value}
              </span>
            </span>
            {cr.reason && <span className="text-gray-mid break-words">{cr.reason}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function FitGroup({
  label,
  keys,
  labels,
  values,
  testId,
}: {
  label: string;
  keys: readonly string[];
  labels: Record<string, string>;
  values: Record<string, BuyerResearchFlagValue>;
  testId: string;
}) {
  return (
    <div data-testid={testId}>
      <h4 className="m-0 mb-space-1 text-caption uppercase tracking-wide text-gray-mid">{label}</h4>
      <ul className="m-0 p-0 list-none grid gap-[2px]">
        {keys.map((k) => {
          const verdict = fitVerdict(k, values[k]);
          return (
            <li key={k} className="flex items-center justify-between gap-space-2 text-caption">
              <span className="text-onyx">{labels[k] ?? k}</span>
              {verdict.tone === "blocking" ? (
                <ExceptionBadge
                  severity="overdue"
                  className="ml-0"
                  data-flag-key={k}
                  data-verdict-tone={verdict.tone}
                >
                  {verdict.word}
                </ExceptionBadge>
              ) : (
                <span
                  className={verdict.tone === "muted" ? "text-gray-mid" : "text-onyx"}
                  data-flag-key={k}
                  data-verdict-tone={verdict.tone}
                >
                  {verdict.word}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
